/* ===================================================================
   TurnstileBox -- Cloudflare Turnstile (managed, interaction-only) for the
   public auth forms (signup / login / password reset / OTP resend).

   HOW THE SERVER-SIDE VERIFICATION WORKS: these forms call Supabase Auth
   directly from the browser. Supabase has NATIVE Turnstile support: when
   "Enable CAPTCHA protection" is turned on in the Supabase dashboard (Auth ->
   Settings, provider Turnstile, with the SECRET key pasted there), Supabase
   verifies the token we pass as options.captchaToken against Cloudflare on its
   servers before it will create a user / sign in / send a reset. So the secret
   never touches our code, and verification is genuinely server-side.

   FULLY GATED: renders NOTHING and mints no token unless VITE_TURNSTILE_SITE_KEY
   is set at build time. Until it is, every auth form behaves exactly as today.
   Arming is a two-part, coordinated flip (Charlie gates it with Steve):
     1. set VITE_TURNSTILE_SITE_KEY at build (this widget appears + mints tokens)
     2. enable CAPTCHA (Turnstile) in the Supabase dashboard with the secret key
   Both must flip together: enabling Supabase CAPTCHA without shipping the widget
   would break real logins, and vice-versa the token is simply ignored.

   appearance:"interaction-only" keeps the widget invisible for real users; only
   suspicious traffic ever sees an interactive challenge. A tradesman on a phone
   sees nothing.
   =================================================================== */
import { useEffect, useRef } from 'react';

const SITE_KEY: string =
  (import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined) || '';

/** True when a site key is configured at build time. */
export const turnstileEnabled = Boolean(SITE_KEY);

// Load the Turnstile api.js once, shared across every widget instance.
let scriptPromise: Promise<void> | null = null;
function loadTurnstileScript(): Promise<void> {
  if (typeof window === 'undefined') return Promise.resolve();
  if ((window as unknown as { turnstile?: unknown }).turnstile) return Promise.resolve();
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise<void>((resolve, reject) => {
    const s = document.createElement('script');
    s.src =
      'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('Turnstile script failed to load'));
    document.head.appendChild(s);
  });
  return scriptPromise;
}

interface TurnstileApi {
  render: (el: HTMLElement, opts: Record<string, unknown>) => string;
  remove: (id: string) => void;
  reset: (id?: string) => void;
}

/**
 * Renders a managed Turnstile widget and reports the token via onToken.
 * onToken('') is fired when the token errors or expires so the caller can
 * treat it as "no token yet". Remount the component (change its React `key`)
 * to force a fresh token after a failed auth attempt, since tokens are
 * single-use.
 */
export function TurnstileBox({
  onToken,
}: {
  onToken: (token: string) => void;
}) {
  const boxRef = useRef<HTMLDivElement | null>(null);
  const widgetId = useRef<string | null>(null);

  useEffect(() => {
    if (!SITE_KEY) return;
    let cancelled = false;
    loadTurnstileScript()
      .then(() => {
        if (cancelled || !boxRef.current) return;
        const ts = (window as unknown as { turnstile?: TurnstileApi }).turnstile;
        if (!ts) return;
        widgetId.current = ts.render(boxRef.current, {
          sitekey: SITE_KEY,
          appearance: 'interaction-only',
          callback: (token: string) => onToken(token),
          'error-callback': () => onToken(''),
          'expired-callback': () => onToken(''),
        });
      })
      .catch(() => {
        /* script/network failure: leave token empty. Supabase CAPTCHA, when on,
           will reject an empty token; when off, the empty token is ignored. */
      });
    return () => {
      cancelled = true;
      try {
        const ts = (window as unknown as { turnstile?: TurnstileApi }).turnstile;
        if (ts && widgetId.current) ts.remove(widgetId.current);
      } catch {
        /* ignore */
      }
    };
    // onToken from useState setters is stable; safe as a dep.
  }, [onToken]);

  if (!SITE_KEY) return null;
  return <div ref={boxRef} className="turnstile-box" style={{ marginTop: 10 }} />;
}
